import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { get } from 'lodash';
import Header from 'components/Header/Header';
import PatientSearchForm from './PatientSearchForm';
import { IPatientSearchCriteria } from './_types';
import { searchPatients, deletePatient } from './_api';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import useAlerts from 'components/Alerts/useAlerts';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';
import { getColumns, GRID_PATIENTS_SETTINGS_KEY, getFamilyName, getGivenName } from './_grid';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useActions } from 'utils/hooks/useActions';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { useEntityRemove } from 'utils/hooks/useEntityRemove';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { useWithTitle } from 'utils/hooks/useWithTitle';

const Patients: React.FC = () => {
  const { t } = useTranslation('Patients');

  useWithTitle();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reload = searchParams.get('reload');
  const { toggleLoader } = useAppGlobals();
  const { addErrorAlert } = useAlerts();
  const { confirmationData, patients, patientSearchFilter } = useAppInfo();
  const { storeConfirmationData, storePatients, storePatientSearchFilter } = useActions();
  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(
    GRID_PATIENTS_SETTINGS_KEY,
  );

  useEffect(() => {
    if (reload === 'true' || (patientSearchFilter && patients.length === 0)) {
      reloadPatients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const handleSearch = async (searchCriteria: IPatientSearchCriteria) => {
    toggleLoader(true);
    storePatientSearchFilter(JSON.stringify(searchCriteria));
    try {
      const results = await searchPatients(searchCriteria);

      if (results === false) {
        addErrorAlert(t('searchError'));
        storePatients([]);
      } else {
        storePatients(results);
      }
    } catch (error) {
      addErrorAlert(t('searchError'));
      storePatients([]);
    } finally {
      toggleLoader(false);
    }
  };

  const reloadPatients = async () => {
    toggleLoader(true);
    try {
      const lastSearchCriteria = patientSearchFilter ? JSON.parse(patientSearchFilter) : {};
      const results = await searchPatients(lastSearchCriteria);
      if (results === false) {
        addErrorAlert(t('loadError'));
        storePatients([]);
      } else {
        storePatients(results);
      }
    } catch (error) {
      addErrorAlert(t('loadError'));
      storePatients([]);
    } finally {
      toggleLoader(false);
    }
  };

  const onCustomEntityView = (row: any) => {
    navigate(`/patients/${row.id}`);
  };

  const onCustomEntityEdit = (row: any) => {
    navigate(`/patients/edit/${row.id}?back=/patients`);
  };

  const onDeleteClick = (row: any) => {
    const familyName = getFamilyName(row.name);
    const givenName = getGivenName(row.name);
    const name = `${familyName} ${givenName}`.trim();
    storeConfirmationData({ id: get(row, 'id'), name });
  };

  const { onEntityRemove } = useEntityRemove(deletePatient, t, reloadPatients);

  const clearAction = () => {
    storeConfirmationData(null);
  };

  const confirmAction = async () => {
    await onEntityRemove(get(confirmationData, 'id'));
    clearAction();
  };

  const columns = reorderColumnsByGridSettings(
    injectColumnWidthsIntoColumns(
      getColumns(t, onCustomEntityView, onCustomEntityEdit, onDeleteClick),
      200,
    ),
  );

  return (
    <>
      <Header title={t('title')} addUrl="/patients/create" />
      <PatientSearchForm onSubmit={handleSearch} />
      <MuiGrid
        gridKey={GRID_PATIENTS_SETTINGS_KEY}
        rows={patients}
        columns={columns}
        autoHeight={false}
        hideHeaderFilters={false}
      />
      {confirmationData && confirmationData.id && !confirmationData.editDialog && (
        <ConfirmationDialog
          title={t('deleteConfirm', { name: confirmationData.name })}
          open={true}
          aria-labelledby="form-dialog-title"
          fullWidth={true}
          cancelAction={clearAction}
          confirmAction={confirmAction}
        />
      )}
    </>
  );
};

export default Patients;
